import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { getBranches, getTrialBalance } from "../../../../Api/apiClient";
import {
  calculateDateRange,
  getRangeSelectionLabel,
} from "../../Shared/Utils/dateRanges";
import {
  exportTrialBalanceToCSV,
  exportTrialBalanceToPDF,
} from "../Helpers/exportHelpers";

export const useTrialBalanceData = () => {
  const toast = useToast();
  const initialDates = useMemo(() => calculateDateRange("tq"), []);
  const initialFilters = useMemo(
    () => ({
      from_date: initialDates.fromDate,
      to_date: initialDates.toDate,
      branches: [],
      include_zero_balances: false,
      with_pnl_openings: false,
      rangeKey: "tq",
      rangeLabel: getRangeSelectionLabel(
        "tq",
        { reports: {} },
        initialDates.fromDate,
        initialDates.toDate
      ),
    }),
    [initialDates]
  );

  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [branches, setBranches] = useState([]);
  const [isLoadingReport, setIsLoadingReport] = useState(true);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const hasLoadedInitialReport = useRef(false);

  const fetchBranches = useCallback(async () => {
    setIsLoadingBranches(true);
    try {
      const response = await getBranches();
      setBranches(
        (response.data.results || []).map((branch) => ({
          id: branch.id,
          name: branch.name.ar || branch.name.en || branch.id,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch branches:", error);
      toast({
        title: "Error",
        description: "Could not load branch options.",
        status: "error",
        duration: 2000,
      });
    } finally {
      setIsLoadingBranches(false);
    }
  }, [toast]);

  const fetchReportData = useCallback(
    async (nextFilters) => {
      setIsLoadingReport(true);
      setFilters(nextFilters);

      try {
        const response = await getTrialBalance(nextFilters);
        setReportData(response.data);
      } catch (error) {
        console.error("Failed to fetch trial balance report data:", error);
        toast({
          title: "Error",
          description: "Could not load trial balance report data.",
          status: "error",
        });
        setReportData(null);
      } finally {
        setIsLoadingReport(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    if (hasLoadedInitialReport.current) {
      return;
    }

    hasLoadedInitialReport.current = true;
    fetchReportData(initialFilters);
  }, [fetchReportData, initialFilters]);

  const handleDateRangeSelect = useCallback(
    (range) => {
      const { fromDate, toDate } = calculateDateRange(range);
      fetchReportData({
        ...filters,
        from_date: fromDate,
        to_date: toDate,
        rangeKey: range,
        rangeLabel: getRangeSelectionLabel(range, { reports: {} }, fromDate, toDate),
      });
    },
    [fetchReportData, filters]
  );

  const handleApplyCustomRange = useCallback(
    ({ from_date, to_date }) => {
      fetchReportData({
        ...filters,
        from_date,
        to_date,
        rangeKey: "custom",
        rangeLabel: getRangeSelectionLabel("custom", { reports: {} }, from_date, to_date),
      });
    },
    [fetchReportData, filters]
  );

  const handleApplySettings = useCallback(
    (settings) => {
      fetchReportData({
        ...filters,
        ...settings,
      });
    },
    [fetchReportData, filters]
  );

  const handleExport = useCallback(async (format) => {
    setIsExporting(true);
    try {
      if (format === "pdf") {
        exportTrialBalanceToPDF(reportData, filters, toast);
      } else {
        exportTrialBalanceToCSV(reportData, filters, toast);
      }
    } finally {
      setIsExporting(false);
    }
  }, [filters, reportData, toast]);

  return {
    branches,
    filters,
    isExporting,
    isLoadingBranches,
    isLoadingReport,
    reportData,
    fetchBranches,
    fetchReportData,
    handleApplyCustomRange,
    handleApplySettings,
    handleDateRangeSelect,
    handleExport,
  };
};
